/** Contexto de render: WebGLRenderer + Scene + câmera + luz + neblina + resize + bloom. */
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { quality } from "../core/Quality";
import { GradePass, DEFAULT_GRADE, type GradeParams } from "./GradePass";

export interface RenderTheme {
  ambient: number;
  ambientIntensity: number;
  sun: number;
  sunIntensity: number;
  fog: number;
}

const NIGHT_SKY = new THREE.Color(0x10142c); // 10.5V
const FLASH_SKY = new THREE.Color(0xd8e6ff);
const _col = new THREE.Color();
/** 10.9BQ — intensidades BASE do preenchimento (construtor e ciclo dia/noite leem daqui). */
const AMBIENT_BASE = 0.42;
const HEMI_BASE = 0.62;

export class Renderer {
  readonly scene = new THREE.Scene();
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  // 10.5V — ciclo dia/noite
  private theme: RenderTheme | null = null;
  /** 10.9BS — céu/névoa da REGIÃO (base do ciclo dia/noite). Sem mood, cai no `theme.fog`. */
  private moodSky: THREE.Color | null = null;
  private moodFog: THREE.Color | null = null;
  private dayFactor = 1;
  private flash = 0;
  private pixelCap = 2;
  private hemi!: THREE.HemisphereLight;
  private ambient!: THREE.AmbientLight;
  private moon!: THREE.DirectionalLight;
  // 10.7M — pós-processamento (bloom)
  private composer: EffectComposer | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  // 10.9GR — acabamento (vinheta + grão + cor por região) e bloom por região
  private gradePass: GradePass | null = null;
  private grade: GradeParams = DEFAULT_GRADE;
  private gradeOverride: GradeParams | null = null;
  private bloomStrength = 0.45;
  private lastTick = 0;
  /** 10.9F — governador de FPS ESCALONADO (não mexe no preset salvo):
   *  0 = cheio · 1 = sem bloom · 2 = sem sombras · 3 = resolução baixa (pixelRatio 1). */
  private perfLevel = 0;

  constructor(container: HTMLElement) {
    const q = quality.values;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    // mobile: limita pixelRatio pelo preset (evita render a 3x em telas densas)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, q.pixelRatioCap) * q.renderScale); // 10.9IE
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = q.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // 10.9BQ — UM SÓ ESTÁGIO DE COR: o CSS filter do canvas (saturate/contrast/brightness) foi
    // removido; a exposição sobe de 0.98 → 1.05 pra compensar o brightness que ele dava, e o
    // bloom fica mais seletivo (threshold 0.85: só energia de verdade brilha).
    this.renderer.toneMappingExposure = 1.05;
    container.appendChild(this.renderer.domElement);

    const skyColor = 0xbfe0d0;
    this.scene.background = new THREE.Color(skyColor);
    // 10.5M — mapa 3x maior: névoa mais leve (0.014 apagava tudo além de ~40 m e deixava o
    // mundo com aquele "lavado" branco-esverdeado).
    this.scene.fog = new THREE.FogExp2(skyColor, 0.0085);

    this.pixelCap = q.pixelRatioCap;
    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 600);

    // Luz clara/quente: sol direcional (sombras) + hemisférica céu/grama + ambiente médio.
    // 10.6B — o preenchimento (hemi + ambient) estava alto demais: apagava as sombras e
    // deixava tudo com o mesmo valor de cinza. Baixei o preenchimento e subi o sol.
    // 10.9BQ: preenchimento um pouco mais alto (hemi .58→.62, ambient .36→.42) pra não escurecer
    // sem o CSS filter. Os mesmos bases valem em applyLighting (ciclo dia/noite).
    const hemi = new THREE.HemisphereLight(0xbfe2ff, 0x5c4d33, HEMI_BASE); // céu azul + bounce quente do chão
    hemi.layers.enable(7); // 10.9GX: luzes também iluminam a prévia 3D
    this.scene.add(hemi);
    this.hemi = hemi;

    const ambient = new THREE.AmbientLight(0xdfe6f0, AMBIENT_BASE);
    ambient.layers.enable(7);
    this.scene.add(ambient);
    this.ambient = ambient;

    const moon = new THREE.DirectionalLight(0xfff2d6, 1.35);
    moon.position.set(30, 48, 20);
    moon.castShadow = q.shadows;
    moon.shadow.mapSize.set(q.shadowMapSize, q.shadowMapSize);
    moon.shadow.camera.near = 1;
    moon.shadow.camera.far = 160;
    const s = 55;
    moon.shadow.camera.left = -s;
    moon.shadow.camera.right = s;
    moon.shadow.camera.top = s;
    moon.shadow.camera.bottom = -s;
    moon.shadow.bias = -0.0004;
    moon.layers.enable(7);
    this.scene.add(moon);
    this.scene.add(moon.target);
    this.moon = moon;

    // 10.5E: luz de CONTORNO (rim) fria, oposta ao sol, sem sombra — separa silhuetas do chão
    // 10.6B: mais forte e mais rasante. Sem outline (caro: 36–45 meshes por criatura), é ela
    // que desenha a borda clara que descola o monstro do chão.
    const rim = new THREE.DirectionalLight(0x9fc8ff, 0.78);
    rim.position.set(-26, 11, -32);
    rim.layers.enable(7);
    this.scene.add(rim);

    this.setupComposer(q.bloom);

    // 10.9BR — medição em DEV: `__vh.drawCalls()` / `__vh.info()` no console do navegador
    if (import.meta.env.DEV) {
      const r = this.renderer;
      (window as unknown as { __vh: unknown }).__vh = {
        drawCalls: () => r.info.render.calls,
        info: () => ({ calls: r.info.render.calls, triangles: r.info.render.triangles, geometries: r.info.memory.geometries, textures: r.info.memory.textures, programs: r.info.programs?.length ?? 0 }),
      };
    }
  }

  /** 10.7M — monta o EffectComposer com bloom. Sem bloom (preset low) usa render direto. */
  private setupComposer(bloom: boolean): void {
    if (!bloom) { this.composer = null; this.bloomPass = null; return; }
    const w = window.innerWidth, h = window.innerHeight;
    const composer = new EffectComposer(this.renderer);
    composer.setPixelRatio(Math.min(window.devicePixelRatio, this.pixelCap) * quality.values.renderScale); // 10.9IE
    composer.setSize(w, h);
    composer.addPass(new RenderPass(this.scene, this.camera));
    // brilho só nos emissivos fortes (cristais, armas, projéteis, olhos dos monstros)
    // 10.9BQ: strength .55→.45, radius .5→.45, threshold .82→.85 — só energia de verdade floresce
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), this.bloomStrength, 0.45, 0.85);
    composer.addPass(bloomPass);
    // 10.9GR — acabamento em linear, antes do tone mapping
    const grade = new GradePass();
    grade.apply(this.gradeOverride ?? this.grade);
    composer.addPass(grade);
    composer.addPass(new OutputPass()); // aplica ACES tone mapping + sRGB no fim
    this.composer = composer;
    this.bloomPass = bloomPass;
    this.gradePass = grade;
  }

  /** Aplica o tema visual da região atual (luz/ambiente/névoa). Iluminação clara e legível
   *  (Fase 8.1): key light mais forte p/ personagem/inimigos/terreno legíveis, sem lavar. */
  applyTheme(t: RenderTheme): void {
    this.theme = t; // 10.5V: guardado para o ciclo dia/noite reaplicar por cima
    this.applyLighting();
  }

  /**
   * 10.5V — CICLO DIA/NOITE + clima. `day` 0 = noite fechada, 1 = meio-dia; `flash` 0..1 é o
   * clarão do relâmpago. A luz da região continua sendo a base — aqui só modulamos.
   */
  setDayFactor(day: number, flash = 0): void {
    this.dayFactor = Math.max(0, Math.min(1, day));
    this.flash = Math.max(0, Math.min(1, flash));
    this.applyLighting();
  }
  get dayLight(): number {
    return this.dayFactor;
  }

  /**
   * 10.9BS — CLIMA VISUAL POR REGIÃO. Antes o céu era a constante 0xbfe0d0 e a névoa vinha só
   * do `theme.fog`; agora cada região manda a sua cor de céu, cor de névoa e densidade
   * (Floresta leve, Tempestade pesada). O ciclo dia/noite escurece A PARTIR destas cores.
   */
  setRegionMood(mood: { sky: number; fog: number; density: number; grade?: GradeParams; bloom?: number }): void {
    this.moodSky = (this.moodSky ?? new THREE.Color()).setHex(mood.sky);
    this.moodFog = (this.moodFog ?? new THREE.Color()).setHex(mood.fog);
    // 10.9HZ — a densidade da região é multiplicada pelo preset (baixo fecha mais a névoa
    // pra esconder o corte de cenário mais curto). Guardada pra reaplicar ao trocar de preset.
    this.moodDensity = mood.density;
    (this.scene.fog as THREE.FogExp2).density = mood.density * quality.values.fogBoost;
    // 10.9GR — cor/bloom da região
    this.grade = mood.grade ?? DEFAULT_GRADE;
    this.bloomStrength = mood.bloom ?? 0.45;
    this.applyGrade();
    this.applyLighting();
  }

  /** 10.9GR — acabamento de LUGAR (Fenda dos Titãs, raid) por cima do da região; null = volta. */
  setGradeOverride(g: GradeParams | null): void {
    this.gradeOverride = g;
    this.applyGrade();
  }
  private applyGrade(): void {
    this.gradePass?.apply(this.gradeOverride ?? this.grade);
    if (this.bloomPass) this.bloomPass.strength = this.gradeOverride ? this.bloomStrength + 0.12 : this.bloomStrength;
  }
  /** 10.9GR — clarão branco na tela (dano forte no Caçador, relâmpago). 0..0.6, decai sozinho. */
  screenFlash(strength: number): void {
    this.gradePass?.flash(strength);
  }

  private applyLighting(): void {
    const t = this.theme;
    if (!t) return;
    const d = this.dayFactor;
    const f = this.flash;
    const lit = 0.35 + 0.65 * d + f * 0.5; // noite escurece de verdade, mas sem cegar (mobile)
    this.ambient.color.setHex(t.ambient);
    this.ambient.intensity = AMBIENT_BASE * t.ambientIntensity * (0.42 + 0.58 * d) + f * 0.35;
    this.hemi.color.setHex(t.sun);
    this.hemi.intensity = HEMI_BASE * t.ambientIntensity * lit;
    this.moon.color.setHex(t.sun);
    this.moon.intensity = 2.45 * t.sunIntensity * (0.28 + 0.72 * d) + f * 1.2;
    // céu/névoa escurecem e esfriam à noite — 10.9BS: a partir da cor da REGIÃO (mood), não
    // de uma constante; céu e névoa têm cores próprias (a névoa um tom abaixo do céu)
    const night = (1 - d) * 0.85;
    if (this.moodSky) _col.copy(this.moodSky); else _col.setHex(t.fog);
    _col.lerp(NIGHT_SKY, night);
    if (f > 0) _col.lerp(FLASH_SKY, f * 0.6);
    (this.scene.background as THREE.Color).copy(_col);
    if (this.moodFog) _col.copy(this.moodFog); else _col.setHex(t.fog);
    _col.lerp(NIGHT_SKY, night);
    if (f > 0) _col.lerp(FLASH_SKY, f * 0.6);
    (this.scene.fog as THREE.FogExp2).color.copy(_col);
  }

  /** 10.5J: a câmera de SOMBRA segue o player. Fixa na origem, longe da base o frustum (±55)
   *  acabava no meio da tela e virava um retângulo cinza no chão. */
  followShadow(x: number, z: number): void {
    this.moon.position.set(x + 30, 48, z + 20);
    this.moon.target.position.set(x, 0, z);
    this.moon.target.updateMatrixWorld();
  }

  /** 10.9HZ — densidade de névoa da REGIÃO atual, sem o multiplicador do preset. */
  private moodDensity = 0.0085;

  /** 10.9HZ — o governador derrubou a qualidade; uma escolha MANUAL do jogador zera os degraus
   *  (senão escolher "Alto" nas Configurações não mudava nada na imagem pelo resto da sessão). */
  resetDegrade(): void {
    this.perfLevel = 0;
  }

  /** 10.4X: reaplica o preset de qualidade em runtime (seletor de gráficos + governador de FPS). */
  /** 10.9IT — o canvas de verdade (pra ouvir perda de contexto WebGL). */
  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  applyQuality(): void {
    const q = quality.values;
    // 10.9F — o perfLevel só REDUZ por cima do preset (nunca aumenta além do escolhido)
    const wantShadows = q.shadows && this.perfLevel < 2;
    const cap = this.perfLevel >= 3 ? 1.0 : q.pixelRatioCap;
    this.pixelCap = cap;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap) * quality.values.renderScale); // 10.9IE
    this.renderer.shadowMap.enabled = wantShadows;
    this.renderer.shadowMap.needsUpdate = true;
    this.moon.castShadow = wantShadows;
    if (wantShadows && this.moon.shadow.mapSize.width !== q.shadowMapSize) {
      this.moon.shadow.mapSize.set(q.shadowMapSize, q.shadowMapSize);
      this.moon.shadow.map?.dispose();
      this.moon.shadow.map = null as unknown as THREE.WebGLRenderTarget; // força recriar no tamanho novo
    }
    // bloom conforme o preset E o governador (perfLevel >= 1 desliga)
    const wantBloom = q.bloom && this.perfLevel < 1;
    const hasBloom = this.composer !== null;
    if (wantBloom && !hasBloom) this.setupComposer(true);
    else if (!wantBloom && hasBloom) { this.composer?.dispose(); this.composer = null; this.bloomPass = null; this.gradePass = null; }
    // 10.9HZ — névoa acompanha o preset na hora (o corte de cenário mais curto depende dela)
    if (this.scene.fog) (this.scene.fog as THREE.FogExp2).density = this.moodDensity * q.fogBoost;
    // 10.9HZ — a sombra do CENÁRIO é decidida ao construir os chunks; ao trocar de preset em
    // runtime os já construídos mantinham a flag antiga. Reaplica na cena inteira.
    this.scene.traverse((o) => {
      const im = o as THREE.InstancedMesh & { userData: { decorCaster?: boolean } };
      if (im.isInstancedMesh && im.userData.decorCaster) im.castShadow = q.decorShadows;
    });
  }

  /** 10.9F — governador de FPS: baixa UM degrau (bloom→sombras→resolução). Só reduz. */
  degrade(): boolean {
    if (this.perfLevel >= 3) return false;
    this.perfLevel++;
    this.applyQuality();
    return true;
  }
  get perfDegradeLevel(): number {
    return this.perfLevel;
  }

  /**
   * 10.9GX — PRÉVIA 3D de um objeto (o Caçador com a armadura/arma reais) num canvas 2D da UI.
   * Renderiza SÓ a camada 7 (os alvos + as luzes) com uma câmera própria num canto do canvas
   * principal, copia os pixels pro canvas de saída e devolve o viewport — tudo no mesmo quadro,
   * ANTES do render principal (que sobrescreve o canto). Sem renderer/contexto extra.
   */
  renderPreview(targets: THREE.Object3D[], out: HTMLCanvasElement, angle: number, center: THREE.Vector3): void {
    const r = this.renderer;
    const pr = r.getPixelRatio();
    const W = r.domElement.width, H = r.domElement.height;
    const w = Math.min(W, out.width), h = Math.min(H, out.height);
    if (w < 8 || h < 8) return;
    for (const t of targets) t.traverse((o) => o.layers.enable(7));
    const cam = this.previewCam;
    cam.aspect = w / h;
    cam.updateProjectionMatrix();
    cam.layers.set(7);
    cam.position.set(center.x + Math.sin(angle) * 4.6, center.y + 1.35, center.z + Math.cos(angle) * 4.6);
    cam.lookAt(center.x, center.y + 0.95, center.z);
    const bg = this.scene.background;
    this.scene.background = this.previewBg;
    const autoShadow = r.shadowMap.autoUpdate;
    r.shadowMap.autoUpdate = false; // usa o mapa de sombra do quadro anterior
    r.getViewport(this.savedViewport);
    r.setScissorTest(true);
    r.setScissor(0, 0, w / pr, h / pr);
    r.setViewport(0, 0, w / pr, h / pr);
    r.render(this.scene, cam);
    r.setScissorTest(false);
    r.setViewport(this.savedViewport);
    r.shadowMap.autoUpdate = autoShadow;
    this.scene.background = bg;
    const ctx = out.getContext("2d");
    if (ctx) ctx.drawImage(r.domElement, 0, H - h, w, h, 0, 0, out.width, out.height);
  }
  private readonly previewCam = new THREE.PerspectiveCamera(30, 1, 0.1, 60);
  private readonly previewBg = new THREE.Color(0x141028);
  private readonly savedViewport = new THREE.Vector4();

  render(): void {
    if (this.composer) {
      // 10.9GR — grão animado / clarão: relógio próprio (independe do dt do jogo, que congela no hit-stop)
      const now = performance.now();
      const dt = this.lastTick > 0 ? Math.min(0.1, (now - this.lastTick) / 1000) : 0.016;
      this.lastTick = now;
      this.gradePass?.tick(dt, this.camera.aspect);
      this.composer.render();
    } else this.renderer.render(this.scene, this.camera);
  }

  /**
   * Reajusta canvas+câmera ao viewport disponível. NÃO recria renderer/materiais.
   * Reaplica o cap de pixelRatio (DPR pode mudar em foldable/troca de tela).
   */
  resize(width: number, height: number, dpr: number): void {
    this.renderer.setPixelRatio(Math.min(dpr, this.pixelCap) * quality.values.renderScale); // 10.9IE
    this.renderer.setSize(width, height, true);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
    if (this.composer) {
      this.composer.setPixelRatio(Math.min(dpr, this.pixelCap) * quality.values.renderScale); // 10.9IE
      this.composer.setSize(width, height);
      this.bloomPass?.setSize(width, height);
    }
  }

  dispose(): void {
    this.renderer.dispose();
  }
}
